import { toast } from 'sonner';
import { captureAndReportError } from './errorReporting';
import {
  dimensionsMatch,
  getImageDimensions,
  getImageOnlyDimensions,
  type ImageDimensions,
} from './imageDimensions';
export interface EditorsChoiceValidationResult {
  isValid: boolean;
  error?: string;
}

export const validatePreviewDimensions = async (
  file: File,
  baselineDimensions: ImageDimensions | null,
) => {
  try {
    const dimensions = await getImageDimensions(file);

    if (!baselineDimensions) {
      return {
        isValid: true,
        dimensions,
      };
    }

    if (!dimensionsMatch(dimensions, baselineDimensions)) {
      const error = `Media dimensions (${dimensions.width}x${dimensions.height}) don't match required dimensions (${baselineDimensions.width}x${baselineDimensions.height})`;
      toast.error(error);
      return {
        isValid: false,
        error,
      };
    }

    return {
      isValid: true,
      dimensions,
    };
  } catch (error) {
    const errorMsg = 'Failed to validate media dimensions';
    captureAndReportError(errorMsg, error);
    toast.error(errorMsg);
    return {
      isValid: false,
      error: errorMsg,
    };
  }
};

interface ExactDimensionValidationOptions {
  expectedWidth: number;
  expectedHeight: number;
  errorLabel: string;
}

const validateExactDimensions = async (
  file: File,
  options: ExactDimensionValidationOptions,
): Promise<EditorsChoiceValidationResult> => {
  try {
    const dimensions = await getImageOnlyDimensions(file);

    if (
      dimensions.width !== options.expectedWidth ||
      dimensions.height !== options.expectedHeight
    ) {
      const error = `${options.errorLabel} must be exactly ${options.expectedWidth}x${options.expectedHeight} pixels`;
      toast.error(error, {
        description: `Your image is ${dimensions.width}x${dimensions.height} pixels`,
      });
      return { isValid: false, error };
    }

    return { isValid: true };
  } catch {
    const error = `Failed to validate ${options.errorLabel.toLowerCase()} dimensions`;
    toast.error(error);
    return { isValid: false, error };
  }
};

export const validateEditorsChoiceGraphic = (
  file: File,
): Promise<EditorsChoiceValidationResult> => {
  return validateExactDimensions(file, {
    expectedWidth: 1200,
    expectedHeight: 1200,
    errorLabel: "Editor's Choice graphic",
  });
};

export const validateBannerDimensions = (
  file: File,
): Promise<EditorsChoiceValidationResult> => {
  return validateExactDimensions(file, {
    expectedWidth: 1200,
    expectedHeight: 600,
    errorLabel: 'Banner',
  });
};

export const hasAnyPreview = (
  previews: (File | null)[],
  previewUrls?: (string | null)[],
  excludeIndex?: number,
) => {
  const hasFilePreview = previews.some(
    (p, index) => p !== null && index !== excludeIndex,
  );

  const hasUrlPreview =
    previewUrls?.some((url, index) => url !== null && index !== excludeIndex) ??
    false;

  return hasFilePreview || hasUrlPreview;
};
